<template>
  <div class="chart-generator">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 class="page-title">生成图表</h1>
      <button class="close-btn" @click="goBack">×</button>
    </div>

    <!-- 异常域值设置 -->
    <div class="threshold-section">
      <div class="threshold-header">
        <h3 class="section-title">异常域值设置</h3>
        <button class="threshold-set-btn" @click="setThreshold" :disabled="settingThreshold">
          {{ settingThreshold ? '设置中...' : '设置' }}
        </button>
      </div>
      <div class="threshold-grid">
        <div class="threshold-item" v-if="deviceType === '温湿度表'">
          <label>温度上限 </label>
          <div class="input-group">
            <input 
              type="number" 
              v-model="thresholds.temperatureMax" 
              class="threshold-input"
              step="0.1"
            />
            <span class="unit">°C</span>
          </div>
        </div>
        <div class="threshold-item" v-if="deviceType === '温湿度表'">
          <label>温度下限置</label>
          <div class="input-group">
            <input 
              type="number" 
              v-model="thresholds.temperatureMin" 
              class="threshold-input"
              step="0.1"
            />
            <span class="unit">°C</span>
          </div>
        </div>
        <div class="threshold-item" v-if="deviceType === '温湿度表'">
          <label>湿度上限 </label>
          <div class="input-group">
            <input 
              type="number" 
              v-model="thresholds.humidityMax" 
              class="threshold-input"
              step="0.1"
            />
            <span class="unit">%</span>
          </div>
        </div>
        <div class="threshold-item" v-if="deviceType === '温湿度表'">
          <label>湿度下限 </label>
          <div class="input-group">
            <input 
              type="number" 
              v-model="thresholds.humidityMin" 
              class="threshold-input"
              step="0.1"
            />
            <span class="unit">%</span>
          </div>
        </div>
        <div class="threshold-item" v-if="deviceType === '压力表'">
          <label>压力上限</label>
          <div class="input-group">
            <input 
              type="number" 
              v-model="thresholds.pressureMax" 
              class="threshold-input"
              step="0.01"
            />
            <span class="unit">MPa</span>
          </div>
        </div>
        <div class="threshold-item" v-if="deviceType === '压力表'">
          <label>压力下限</label>
          <div class="input-group">
            <input 
              type="number" 
              v-model="thresholds.pressureMin" 
              class="threshold-input"
              step="0.01"
            />
            <span class="unit">MPa</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="chart-section">
      <div class="chart-header">
        <h3 class="chart-title">{{ deviceName }}</h3>
        <button class="download-btn" @click="downloadChart">
          <span class="download-icon">📥</span>
        </button>
      </div>
      
      <!-- 温湿度表显示两个图表 -->
      <div v-if="deviceType === '温湿度表'" class="charts-container">
        <div class="chart-wrapper">
          <canvas ref="temperatureChart" class="chart-canvas"></canvas>
        </div>
        <div class="chart-wrapper">
          <canvas ref="humidityChart" class="chart-canvas"></canvas>
        </div>
      </div>
      
      <!-- 压力表显示一个图表-->
      <div v-else-if="deviceType === '压力表'" class="charts-container">
        <div class="chart-wrapper">
          <canvas ref="pressureChart" class="chart-canvas"></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { HTTP_CONFIG } from '@/config/config.js'

export default {
  name: 'ChartGenerator',
  data() {
    return {
      deviceId: '',
      deviceName: '',
      deviceType: '',
      targetDate: '',
      settingThreshold: false,
      thresholds: {
        temperatureMax: 40,
        temperatureMin: 0,
        humidityMax: 80,
        humidityMin: 20,
        pressureMax: 1.0,
        pressureMin: 0.1
      },
      chartData: [],
      charts: {
        temperature: null,
        humidity: null,
        pressure: null
      }
    }
  },
  mounted() {
    this.initFromRoute();
    this.loadChartData();
  },
  methods: {
    initFromRoute() {
      const query = this.$route.query;
      this.deviceId = query.fac_id || '';
      this.deviceName = query.deviceName || '未知设备';
      this.deviceType = query.deviceType || '';
      this.targetDate = query.targetDate || new Date().toISOString().split('T')[0];
    },

    async loadChartData() {
      try {
        const response = await fetch(
          `${HTTP_CONFIG.SERVER_ADDR}/environment/query_meter_dailydata/?fac_id=${this.deviceId}&target_date=${this.targetDate}`
        );
        const data = await response.json();
        
        if (data && data.data) {
          this.chartData = data.data;
          let devStr = this.deviceId.toString()
          let matchRes = devStr.match(/T/g)
          if(matchRes != null) {
            if(data.threshold.length > 0) {
              this.thresholds.temperatureMax = data.threshold[0].temperature_max
              this.thresholds.temperatureMin = data.threshold[0].temperature_min
              this.thresholds.humidityMax = data.threshold[0].humidity_max
              this.thresholds.humidityMin = data.threshold[0].humidity_min
            }
          }
          else {
            if(data.threshold.length > 0) {
              this.thresholds.pressureMax = data.threshold[0].pressure_max
              this.thresholds.pressureMin = data.threshold[0].pressure_min
            }
          }
          this.renderCharts();
        }
      } catch (error) {
        console.error('加载图表数据失败:', error);
        // 使用模拟数据
        this.chartData = this.getMockData();
        this.renderCharts();
      }
    },

    getMockData() {
      if (this.deviceType === '温湿度表') {
        return Array.from({ length: 25 }, (_, i) => ({
          temperature: 25 + Math.sin(i * 0.3) * 5 + Math.random() * 2,
          humidity: 50 + Math.sin(i * 0.2) * 15 + Math.random() * 5,
          record_date: new Date(2024, 0, 1, 8 + Math.floor(i * 0.25), (i * 15) % 60)
        }));
      } else {
        return Array.from({ length: 25 }, (_, i) => ({
          pressure: 0.5 + Math.sin(i * 0.3) * 0.2 + Math.random() * 0.05,
          record_date: new Date(2024, 0, 1, 8 + Math.floor(i * 0.25), (i * 15) % 60)
        }));
      }
    },

    renderCharts() {
      if (this.deviceType === '温湿度表') {
        this.renderTemperatureChart();
        this.renderHumidityChart();
      } else if (this.deviceType === '压力表') {
        this.renderPressureChart();
      }
    },

    renderTemperatureChart() {
      const ctx = this.$refs.temperatureChart;
      if (this.charts.temperature) {
        this.charts.temperature.destroy();
      }
      const labels = this.chartData.map(function(item) {
        let str = item.record_date.toString()
        return str.substr(11, 8)
      });

      this.charts.temperature = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: '温度',
              data: this.chartData.map(item => item.temperature),
              borderColor: '#1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              borderWidth: 2,
              pointRadius: 4,
              pointBackgroundColor: '#1976d2',
              tension: 0.1
            },
            {
              label: '上限',
              data: Array(labels.length).fill(this.thresholds.temperatureMax),
              borderColor: '#f44336',
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              borderWidth: 2,
              pointRadius: 0,
              tension: 0
            },
            {
              label: '下限',
              data: Array(labels.length).fill(this.thresholds.temperatureMin),
              borderColor: '#000000',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderWidth: 2,
              pointRadius: 0,
              tension: 0
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: '温度趋势表',
              font: { size: 16, weight: 'bold' }
            },
            legend: {
              position: 'top'
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              min: Math.min(...this.chartData.map(item => item.temperature)) - 5,
              max: Math.max(...this.chartData.map(item => item.temperature)) + 5,
              title: {
                display: true,
                text: '温度 (°C)'
              }
            },
            x: {
              title: {
                display: true,
                text: '时间'
              }
            }
          }
        }
      });
    },

    renderHumidityChart() {
      const ctx = this.$refs.humidityChart;
      if (this.charts.humidity) {
        this.charts.humidity.destroy();
      }

      const labels = this.chartData.map(function(item) {
        let str = item.record_date.toString()
        return str.substr(11, 8)
      });

      this.charts.humidity = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: '湿度',
              data: this.chartData.map(item => item.humidity),
              borderColor: '#4caf50',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderWidth: 2,
              pointRadius: 4,
              pointBackgroundColor: '#4caf50',
              tension: 0.1
            },
            {
              label: '上限',
              data: Array(labels.length).fill(this.thresholds.humidityMax),
              borderColor: '#f44336',
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              borderWidth: 2,
              pointRadius: 0,
              tension: 0
            },
            {
              label: '下限',
              data: Array(labels.length).fill(this.thresholds.humidityMin),
              borderColor: '#000000',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderWidth: 2,
              pointRadius: 0,
              tension: 0
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: '湿度趋势表',
              font: { size: 16, weight: 'bold' }
            },
            legend: {
              position: 'top'
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              min: Math.min(...this.chartData.map(item => item.humidity)) - 5,
              max: Math.max(...this.chartData.map(item => item.humidity)) + 5,
              title: {
                display: true,
                text: '湿度 (%)'
              }
            },
            x: {
              title: {
                display: true,
                text: '时间'
              }
            }
          }
        }
      });
    },

    renderPressureChart() {
      const ctx = this.$refs.pressureChart;
      if (this.charts.pressure) {
        this.charts.pressure.destroy();
      }

      const labels = this.chartData.map(function(item) {
        let str = item.record_date.toString()
        return str.substr(11, 8)
      });

      this.charts.pressure = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: '压力',
              data: this.chartData.map(item => (item.pressure / 1000).toFixed(2)),
              borderColor: '#ff9800',
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              borderWidth: 2,
              pointRadius: 4,
              pointBackgroundColor: '#ff9800',
              tension: 0.1
            },
            {
              label: '上限',
              data: Array(labels.length).fill(this.thresholds.pressureMax),
              borderColor: '#f44336',
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              borderWidth: 2,
              pointRadius: 0,
              tension: 0
            },
            {
              label: '下限',
              data: Array(labels.length).fill(this.thresholds.pressureMin),
              borderColor: '#000000',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderWidth: 2,
              pointRadius: 0,
              tension: 0
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: '压力趋势表',
              font: { size: 16, weight: 'bold' }
            },
            legend: {
              position: 'top'
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              min: Math.min(...this.chartData.map(item => item.pressure / 1000)) - 0.05,
              max: Math.max(...this.chartData.map(item => item.pressure /1000)) + 0.05,
              title: {
                display: true,
                text: '压力 (MPa)'
              }
            },
            x: {
              title: {
                display: true,
                text: '时间'
              }
            }
          }
        }
      });
    },

    downloadChart() {
      // 实现图表下载功能
      const canvas = this.deviceType === '温湿度表' 
        ? this.$refs.temperatureChart 
        : this.$refs.pressureChart;
      
      const link = document.createElement('a');
      link.download = `${this.deviceName}_${this.targetDate}_图表.png`;
      link.href = canvas.toDataURL();
      link.click();
    },

    async setThreshold() {
      if (!this.deviceId) {
        alert('设备ID不能为空');
        return;
      }

      this.settingThreshold = true;
      try {
        const formData = new FormData();
        formData.append('fac_id', this.deviceId);

        // 根据设备类型添加对应的阈值参数
        if (this.deviceType === '温湿度表') {
          formData.append('temperature_max', parseFloat(this.thresholds.temperatureMax));
          formData.append('temperature_min', parseFloat(this.thresholds.temperatureMin));
          formData.append('humidity_max', parseFloat(this.thresholds.humidityMax));
          formData.append('humidity_min', parseFloat(this.thresholds.humidityMin));
        } else if (this.deviceType === '压力表') {
          formData.append('pressure_max', parseFloat(this.thresholds.pressureMax));
          formData.append('pressure_min', parseFloat(this.thresholds.pressureMin));
        }

        console.log('发送的请求数据:', Object.fromEntries(formData));

        const response = await fetch(`${HTTP_CONFIG.SERVER_ADDR}/environment/set_meter_threshold/`, {
          method: 'POST',
          body: formData
        });

        const result = await response.json();
        console.log('接口返回结果:', result);
        
        if (response.ok) {
          alert('阈值设置成功！');
          // 重新渲染图表以显示新的阈值线
          this.renderCharts();
        } else {
          alert('阈值设置失败：' + (result.message || result.error || '未知错误'));
        }
      } catch (error) {
        console.error('设置阈值失败:', error);
        alert('设置阈值失败：网络错误');
      } finally {
        this.settingThreshold = false;
      }
    },

    goBack() {
      this.$router.go(-1);
    }
  },
  beforeUnmount() {
    // 清理图表实例
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.destroy();
    });
  }
}
</script>

<style scoped>
.chart-generator {
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  background: white;
  padding: 20px 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.page-title {
  font-size: 24px;
  color: #1976d2;
  font-weight: bold;
  border-bottom: 3px solid #1976d2;
  padding-bottom: 8px;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #666;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.threshold-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.threshold-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.threshold-set-btn {
  background: #1976d2;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.threshold-set-btn:hover:not(:disabled) {
  background: #1565c0;
}

.threshold-set-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.threshold-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.threshold-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.threshold-item label {
  font-size: 14px;
  color: #555;
  min-width: 80px;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.threshold-input {
  width: 80px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
}

.threshold-input:focus {
  outline: none;
  border-color: #1976d2;
}

.unit {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.chart-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.chart-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.download-btn {
  background: #4caf50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.download-btn:hover {
  background: #45a049;
}

.download-icon {
  font-size: 16px;
}

.charts-container {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.chart-wrapper {
  height: 400px;
  position: relative;
}

.chart-canvas {
  width: 100% !important;
  height: 100% !important;
}

@media (max-width: 768px) {
  .chart-generator {
    padding: 16px;
  }
  
  .threshold-grid {
    grid-template-columns: 1fr;
  }
  
  .chart-wrapper {
    height: 300px;
  }
}
</style> 
